import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Input } from '../../../components/Form';
import { required } from '../../../utils/validation';

import { OfficialShape, ClubOfficialMemberShape, CountryShape } from './type';
import EditOfficialFieldsStyles from './EditOfficialFields.scss';

const EditOfficialFields = ({
    selectedOfficial,
    setSelectedOfficial,
    officialToEdit,
    countries,
    clubOfficialMembers,
}) => {
    const [name, setName] = useState(officialToEdit ? `${officialToEdit.firstName} ${officialToEdit.lastName}` : '');
    const [error, setError] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        setSuggestions(clubOfficialMembers);
    }, [clubOfficialMembers]);

    const handleNameChange = (e) => {
        const { value } = e.target;
        setName(value);
        setSelectedOfficial(null);

        if (value.length > 0) {
            // Filter the list of club official members based on the user's input value.
            // It combines the firstName and lastName of each member and checks if the
            // input value (converted to lowercase) is included in the full name (also converted to lowercase).
            // This ensures that the search is case-insensitive and finds partial matches.
            const filteredSuggestions = clubOfficialMembers.filter((member) => `${member.firstName} ${member.lastName}`.toLowerCase().includes(value.toLowerCase()));
            setSuggestions(filteredSuggestions);
            setIsDropdownVisible(true);
        } else {
            setSuggestions(clubOfficialMembers);
        }
    };

    const updateSuggestions = () => {
        if (name.length > 0) {
            const filteredSuggestions = clubOfficialMembers.filter((member) => `${member.firstName} ${member.lastName}`.toLowerCase().includes(name.toLowerCase()));
            setSuggestions(filteredSuggestions);
        } else {
            setSuggestions(clubOfficialMembers);
        }
    };

    const handleSelect = (official) => {
        setName(`${official.firstName} ${official.lastName}`);
        setSelectedOfficial(official);
        setIsDropdownVisible(false);
    };

    const handleValidation = () => {
        if (!name && !isDropdownVisible) {
            setError('This field is required');
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
          if (inputRef.current && !inputRef.current.contains(event.target)) {
            setIsDropdownVisible(false);
          }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, []);

    const getCountryValue = () => {
        if (officialToEdit === null && selectedOfficial === null) {
            return '';
        }

        if (officialToEdit) {
            return countries.find(item => item.id === officialToEdit.countryId)?.label || '';
        }

        return selectedOfficial?.country || '';
    };

    return (
        <>
            <div ref={inputRef} className={EditOfficialFieldsStyles.inputContainer}>
                <Input
                    controlled
                    themeV3
                    label="Name *"
                    id="Name"
                    field="Name"
                    validate={required}
                    value={name}
                    onChange={handleNameChange}
                    placeholder={clubOfficialMembers.length === 0 ? 'There are no additional officials to add ...' : 'Type to search by name ...'}
                    onFocus={() => { setIsDropdownVisible(true); updateSuggestions(); setError(null); }}
                    onBlur={handleValidation}
                    error={error}
                    disabled={officialToEdit || clubOfficialMembers.length === 0}
                />
                {isDropdownVisible && suggestions.length > 0 && (
                    <ul className={EditOfficialFieldsStyles.dropdownList}>
                        {suggestions.map((member) => (
                            <li
                                key={member.memberId}
                                className={EditOfficialFieldsStyles.dropdownItem}
                                onClick={() => handleSelect(member)}
                            >
                                {`${member.firstName} ${member.lastName} (${member.country})`}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <Input
                controlled
                themeV3
                label="Country"
                id="countryId"
                field="countryId"
                disabled
                value={getCountryValue()}
            />
            <Input
                controlled
                themeV3
                label="UTR Member Id"
                id="memberId"
                field="memberId"
                disabled
                value={officialToEdit?.memberId || selectedOfficial?.memberId || ''}
            />
        </>
    );
};

EditOfficialFields.propTypes = {
    selectedOfficial: PropTypes.shape(ClubOfficialMemberShape),
    setSelectedOfficial: PropTypes.func.isRequired,
    officialToEdit: PropTypes.shape(OfficialShape),
    countries: PropTypes.arrayOf(PropTypes.shape(CountryShape)).isRequired,
    clubOfficialMembers: PropTypes.arrayOf(PropTypes.shape(ClubOfficialMemberShape)).isRequired,
};

EditOfficialFields.defaultProps = {
    selectedOfficial: null,
    officialToEdit: null,
};

export default EditOfficialFields;
